import * as React from "react";
import { ITableData } from "../../interfaces/ITableData";
import { IColumn, Link, Icon, TooltipHost, IGroup, Spinner, SpinnerSize, IIconStyles, ISpinnerStyles, TooltipOverflowMode, Stack, IStackTokens, IStackItemStyles, IconButton, Point, IPersonaStyles, Persona, PersonaSize } from "@fluentui/react";
import { columnSearchTypes, piiConfig, extensions, sharePointInternalTitleName, SharePointFolderContentTypeIds } from "../../config/constants";
import { getFileTypeIconProps, FileIconType } from "@fluentui/react-file-type-icons";
import { getExtension, getLastSplittedStringItem } from "./stringHelper";
import dayjs from 'dayjs';
import advancedSearchStateManager from "../../../../advancedSearch/advancedSearchStateManager";
// import HoverPreview from "../../../../advancedSearch/HoverPreview";
import { SearchReturnType, SortValEnum } from "../../../../advancedSearch/AdvancedSearchEnums";
import { IAdvancedSearchResult } from "../../../../advancedSearch/AdvancedSearchInterfaces";
import { IViewsDropdown } from "../../interfaces/IViewsDropdown";
import orderBy from "lodash.orderby"
import { IFieldDisplayname } from "../../interfaces/IFieldDisplayname";
import { logger } from "../../../../utilities/logger";
import { SharePointInputTypes } from "../../config/sharepointConstants";
import { detailRowLinkStyles, fileIconHeaderIcon } from "../../config/controlStyles";
import { SearchOpenExtensionHelper } from "./SearchOpenExtensionHelper";
import { getFileItemIdFromRelativePath, getItemWebViewUrl, getListItemWebUrl } from "../../services/GraphService";
import authModule from "../../../../utilities/AuthModule";
import { OpenInBrowser, OpenPdfInBrowser } from "../../../../utilities/BrowserUtils";
import generalModule from "../../../../utilities/GeneralModule";
import { detailsListClassNames } from "../../../../contentManager/Body/ContentBodyFilesFolders";
import { ISearchGridLocalizedText } from "../../interfaces/advancedSearch/ISearchGridLocalizedText";
import { IResultTypeFilter, ResultType } from "../directoryComponents/advancedSearch/components/searchGrid";
import { getGridItemUrl } from "./UrlHelper";

const iconStyles: IIconStyles = {
  root: {
    display: "block"
  }
}

const spinnerStyles: ISpinnerStyles = {
  root: {
    height: 20
  }
}

// const gridRowMultiValueStyle: React.CSSProperties = {
//   minHeight: 18
// }

const gridFileNameStackTokens: IStackTokens = {
  childrenGap: 5
};

const gridFileNameStackItemStyles: IStackItemStyles = {
  root: {
    textOverflow: "ellipsis",
    overflow: "hidden",
    whiteSpace: "nowrap"
  }
}

const gridOptionButtonStackItemStyles: IStackItemStyles = {
  root: {
    height: "100%",
    marginTop: -11,
    marginBottom: -11
  }
}

const pillContainer: React.CSSProperties = {
  display: "inline-flex",
  flexWrap: "wrap",
  gap: 5,
  maxWidth: "100%"
}

const peoplePill: React.CSSProperties = {
  backgroundColor: "#edebe9",
  height: 24,
  borderRadius: 12,
  display: "inline-flex",
  alignItems: "center"
}

const peoplePillPersonaStyles: Partial<IPersonaStyles> = {
  details: {
    paddingLeft: 5
  },
  primaryText: {
    fontSize: 12,
    lineHeight: 18
  }
}

interface IIndexedColumn extends IColumn {
  displayIndex?: number;
}

interface IGridTenantSettings {
  openDocumentsInClientApp?: boolean;
}

interface IGroupedColumn {
  displayName: string;
  displayNameLowerCase: string;
  type?: string;
}

export class DetailListSearchColumnSorterGrid {
  tableData: ITableData;
  mainGroupSortOrder: boolean | "desc" | "asc";
  childGroupSortOrder: boolean | "desc" | "asc";
  altSortOrder: boolean | "desc" | "asc";
  altSortOrder2: boolean | "desc" | "asc";


  constructor(private triggerContextMenu: (ev: any, position?: Point) => void, private columnDispatcher: any, private rowDispatcher: any, private groupSetter: any, private localizedText: ISearchGridLocalizedText, private gridTenantSettings: IGridTenantSettings) {
  }

  public setTableData = (result: IAdvancedSearchResult, selectedView: IViewsDropdown, selectedResultTypeFilter: IResultTypeFilter, fields: any[]): void => {
    this.tableData = this.getTableData(result, true, selectedView, fields);
    this.tableData.columns = this.tableData.columns
      .map(column => ({ ...this.setColumnWayOfDisplay(column), onColumnClick: (_ev: React.MouseEvent<HTMLElement>, column: IColumn) => { this.onColumnClick(_ev, column, selectedView, fields) } }))
      .filter(
        column =>
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.webUrl &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.driveid &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.itemid &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.serverRelativePath &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.contentTypeId &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.uniqueId &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.checkoutUser &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.canEdit &&
          column.fieldName.trim().toLowerCase() !== columnSearchTypes.canDelete &&
          column.fieldName !== columnSearchTypes.modifiedText &&
          column.fieldName !== columnSearchTypes.sizeText &&
          column.fieldName !== columnSearchTypes.siteUrl
      );
    this.columnDispatcher(this.tableData.columns);
    this.rowDispatcher(this.filterTableRows(this.tableData.rows ?? [], selectedResultTypeFilter));
  };

  private filterTableRows = (rows: any[], selectedResultTypeFilter: IResultTypeFilter): any[] => {
    if (!selectedResultTypeFilter || selectedResultTypeFilter.type === ResultType.all) {
      return rows;
    }

    if (selectedResultTypeFilter.type === ResultType.documentSet) {
      return rows.filter(row => row[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.documentSet));
    }

    if (selectedResultTypeFilter.type === ResultType.folder) {
      return rows.filter(row => row[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.folder) && !row[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.documentSet));
    }

    if (selectedResultTypeFilter.type === ResultType.document && selectedResultTypeFilter.extension?.trim()) {
      return rows.filter(row => this.getFileExtension(row.name)?.toLowerCase() === selectedResultTypeFilter.extension.toLowerCase());
    }

    return rows;
  }

  private getTableData = (result: IAdvancedSearchResult, addFileIcons: boolean = true, selectedView: IViewsDropdown, fields: any[]): ITableData => {
    if (!result?.result?.length) {
      return { columns: [], rows: [] };
    }

    if (result.type === SearchReturnType.RecentItems) {
      const templateResult = result?.result[0];
      const columns = Object.keys(templateResult);
      let columnResult = columns
        .filter(column => column !== "sharepointIds" && column !== "isDocument")
        .map<IColumn>((item, index) => {
          return {
            key: index.toString(),
            name: item,
            data: templateResult[item]?.type,
            fieldName: item.toLowerCase(),
            minWidth: 120,
            isResizable: true,
            iconClassName: "",
            isIconOnly: false
          };
        });

      if (addFileIcons) {
        columnResult.unshift({
          key: "fileIcons",
          name: "File Type",
          iconClassName: "",
          fieldName: "fileType",
          isIconOnly: true,
          minWidth: 20,
          maxWidth: 20,
          isResizable: false
        });
      }

      let rows = this.getValues(result?.result, columns);
      return { columns: columnResult, rows };
    }

    const uniqueResultDriveIds: string[] = [];
    const uniqueColumnNames: string[] = [];
    let uniqueColumns: IIndexedColumn[] = addFileIcons
      ? [{
        displayIndex: -101,
        key: "fileIcons",
        name: "File Type",
        iconClassName: fileIconHeaderIcon,
        iconName: 'Page',
        fieldName: "fileType",
        isIconOnly: true,
        minWidth: 20,
        maxWidth: 20,
        isResizable: false
      }]
      : [];

    const allowedFieldCount: number = advancedSearchStateManager.allowedAdvancedSearchFields?.length ?? 0;

    for (let i = 0; i < result?.result?.length; i++) {
      const resultElem = result?.result[i];
      const driveId = resultElem.driveId;
      if (!uniqueResultDriveIds.some(uniqueDriveId => uniqueDriveId === driveId)) {
        uniqueResultDriveIds.push(driveId);

        const columnNames = Object.keys(resultElem);
        for (let x = 0; x < columnNames.length; x++) {
          const columnName = columnNames[x];
          if (!uniqueColumnNames.some(uniqueColumnName => uniqueColumnName === columnName)) {
            uniqueColumnNames.push(columnName);

            let displayIndex: number = x;
            const fieldName: string = columnName.toLowerCase();
            if (fieldName === columnSearchTypes.name) {
              // Ensure that name is always displayed first
              displayIndex = -100;
            }
            else if (fieldName === sharePointInternalTitleName || fieldName === "Titel") {
              displayIndex = -99;
            }
            else {
              const allowedFieldIndex: number = advancedSearchStateManager.allowedAdvancedSearchFields?.indexOf(fieldName) ?? -1;
              if (allowedFieldIndex === -1) {
                displayIndex = allowedFieldCount + ((SortValEnum[resultElem[columnName]?.type?.toLowerCase()] ?? SortValEnum.other) as number);
              }
              else {
                displayIndex = allowedFieldIndex;
              }
            }

            uniqueColumns.push({
              displayIndex: displayIndex,
              key: columnName,
              name: columnName,
              data: resultElem[columnName]?.type,
              fieldName: fieldName,
              minWidth: 120,
              isResizable: true,
              iconClassName: "",
              isIconOnly: false
            });
          }
        }
      }
    }

    let rows = this.getValues(result.result, uniqueColumnNames);
    if (selectedView && selectedView.fields.length > 0) {
      //only do these expensive calcs if we actually must
      const fieldsDisplayName: IFieldDisplayname[] = this.getFieldsDisplayName(selectedView, fields);
      uniqueColumns = uniqueColumns.filter(col => {
        const tmp = fieldsDisplayName.find(field => (field.displayName?.toLowerCase() === col.key?.toLowerCase()) || (col.key?.toLowerCase() === "name") || (col.key?.toLowerCase() === "fileicons"));
        return !!tmp
      }) //Filter out columns we don't have, but keep the file name and icon. 

      uniqueColumns.sort((a, b) => {
        const fieldA = fieldsDisplayName.find(f => f.displayName?.toLowerCase() === a.key.toLowerCase());
        const fieldB = fieldsDisplayName.find(f => f.displayName?.toLowerCase() === b.key.toLowerCase());
        return fieldA && fieldB ? fieldA.index - fieldB.index : 1; //if not found, put it at the end of the array
      });

      rows = this.setOrderAndGroups(uniqueColumns, selectedView, fields, rows);

    } else {
      uniqueColumns.sort((a, b) => a.displayIndex < b.displayIndex ? -1 : 1);

      //Default sort - Created
      let createdCol = uniqueColumns.find(x => x.key == "Created");
      if (createdCol) {
        createdCol.isSorted = true;
        createdCol.isSortedDescending = true;
      }
    }
    return { columns: uniqueColumns, rows };
  }

  private setOrderAndGroups = (columns: IIndexedColumn[], selectedView: IViewsDropdown, fields: any[], rows: any[], orderProp?: IColumn, ascending?: boolean): any[] => {
    try {
      const viewOrderBy = selectedView?.query?.OrderBy;
      const altOrderbyCols: string[] = [];

      if (!orderProp && viewOrderBy && viewOrderBy[0].FieldRef) {
        viewOrderBy[0].FieldRef.forEach(fieldRef => {
          let order_desc = fieldRef?.$?.Ascending === 'FALSE' ? true : false;
          let order_name = (fieldRef?.$?.Name as string).toLowerCase();

          let field = fields.find(field => field.InternalName?.toLowerCase() === order_name);

          if (field) {
            let uniqueCol = columns.find(x => x.key == field?.xml.DisplayName);
            if (uniqueCol) {

              altOrderbyCols.push((field?.xml.DisplayName as string).toLowerCase());

              //First sorting column
              if (altOrderbyCols.length == 1) {
                this.altSortOrder = order_desc ? 'desc' : 'asc';
                uniqueCol.isSorted = true;
                uniqueCol.isSortedDescending = order_desc;
              }
              //Second sorting column
              if (altOrderbyCols.length == 2) {
                this.altSortOrder2 = order_desc ? 'desc' : 'asc';
                uniqueCol.isSorted = true;
                uniqueCol.isSortedDescending = order_desc;
              }
            }
          }
        });
      }

      const groupBy = selectedView?.query?.GroupBy;
      if (groupBy?.length > 0) {
        const grouping = groupBy[0];
        const group1 = (grouping?.FieldRef[0]?.$)?.Name;
        const group2 = grouping?.FieldRef?.length > 1 ? (grouping?.FieldRef[1]?.$)?.Name : "";

        const mainGroupField = fields.find(field => field.InternalName?.toLowerCase() === group1?.toLowerCase());
        const childGroupField = fields.find(field => field.InternalName?.toLowerCase() === group2?.toLowerCase());

        if (mainGroupField) {
          const mainGroupColum: IGroupedColumn = {
            displayName: mainGroupField.xml.DisplayName as string,
            displayNameLowerCase: mainGroupField.xml.DisplayName.toLowerCase() as string,
            type: mainGroupField.xml.Type.toLowerCase() as string
          }

          const childGroupColum: IGroupedColumn = {
            displayName: childGroupField?.xml.DisplayName as string,
            displayNameLowerCase: childGroupField?.xml.DisplayName.toLowerCase() as string,
            type: childGroupField?.xml.Type.toLowerCase() as string
          }

          if (!this.mainGroupSortOrder) {
            this.mainGroupSortOrder = (grouping?.FieldRef[0]?.$)?.Ascending === 'FALSE' ? 'desc' : 'asc';

            let mainGroupGridCol = columns.find(x => x.key === mainGroupColum.displayName);


            if (mainGroupGridCol) {
              mainGroupGridCol.isSorted = true;
              mainGroupGridCol.isSortedDescending = this.mainGroupSortOrder === 'desc' ? true : false;
            }

          } else if (orderProp && orderProp.name.toLowerCase() !== mainGroupColum.displayNameLowerCase) {
            let mainGroupGridCol = columns.find(x => x.key === mainGroupColum.displayName);

            if (mainGroupGridCol) {
              mainGroupGridCol.isSorted = true;
              mainGroupGridCol.isSortedDescending = this.mainGroupSortOrder === 'desc' ? true : false;
            }
          }

          if (!this.childGroupSortOrder) {
            this.childGroupSortOrder = grouping?.FieldRef?.length > 1 ? ((grouping?.FieldRef[1]?.$)?.Ascending === 'FALSE' ? 'desc' : 'asc') : undefined;

            let childGroupGridCol = columns.find(x => x.key === childGroupColum?.displayName);

            if (childGroupGridCol) {
              childGroupGridCol.isSorted = true;
              childGroupGridCol.isSortedDescending = this.childGroupSortOrder === 'desc' ? true : false;
            }
          } else if (orderProp && childGroupColum && orderProp.name.toLowerCase() !== childGroupColum.displayNameLowerCase) {
            let childGroupGridCol = columns.find(x => x.key === childGroupColum?.displayName);
            if (childGroupGridCol) {
              childGroupGridCol.isSorted = true;
              childGroupGridCol.isSortedDescending = this.childGroupSortOrder === 'desc' ? true : false;
            }
          }

          const mainSorter = row => {
            const formattedMainGroupValue: string | number = this.getGroupedColumnFormattedValue(mainGroupColum, row[mainGroupColum.displayNameLowerCase]);
            if (typeof formattedMainGroupValue === 'string') {
              return formattedMainGroupValue.toLowerCase();
            } else {
              return formattedMainGroupValue;
            }
          };
          const childSorter = row => {
            const formattedChildGroupValue: string | number = this.getGroupedColumnFormattedValue(childGroupColum, row[childGroupColum.displayNameLowerCase]);
            if (typeof formattedChildGroupValue === 'string') {
              return formattedChildGroupValue?.toLowerCase();
            } else {
              return formattedChildGroupValue;
            }
          }

          let orderGroupNames = childGroupField ? [mainSorter, childSorter] : [mainSorter];

          if (orderProp) {
            if (orderProp.name.toLowerCase() === mainGroupColum.displayNameLowerCase) {
              //Sorting on main group column
              let mainGroupGridCol = columns.find(x => x.key === mainGroupColum.displayName);

              if (mainGroupGridCol?.isSorted) {
                this.mainGroupSortOrder = mainGroupGridCol.isSortedDescending ? 'desc' : 'asc';
              }

              let loadashOrderDirections = childGroupField ? [this.mainGroupSortOrder, this.childGroupSortOrder] : [this.mainGroupSortOrder];;
              rows = orderBy(rows, orderGroupNames, loadashOrderDirections);

            } else if (orderProp.name.toLowerCase() === childGroupColum.displayNameLowerCase) {
              //Sorting on child group column

              let childGroupGridCol = columns.find(x => x.key === childGroupColum.displayName);
              if (childGroupGridCol?.isSorted) {
                this.childGroupSortOrder = childGroupGridCol.isSortedDescending ? 'desc' : 'asc';
              }

              let loadashOrderDirections = [this.mainGroupSortOrder, this.childGroupSortOrder];
              rows = orderBy(rows, orderGroupNames, loadashOrderDirections);
            } else {
              //Sorting on another column            
              const otherSorter = row => {
                const lcOrderProp = orderProp.name.toLowerCase();
                if (typeof row[lcOrderProp] === "string") {
                  return row[lcOrderProp].toLowerCase();
                } else {
                  return row[lcOrderProp];
                }
              }

              let otherSorterCol = columns.find(x => x.key === orderProp.name);

              orderGroupNames.push(otherSorter);

              let groupByOrderDirections: (boolean | "desc" | "asc")[] = childGroupField ? [this.mainGroupSortOrder, this.childGroupSortOrder] : [this.mainGroupSortOrder];
              let otherDirections: (boolean | "desc" | "asc")[] = [otherSorterCol?.isSortedDescending ? 'desc' : 'asc'];
              let loadashOrderDirections = groupByOrderDirections.concat(otherDirections);
              rows = orderBy(rows, orderGroupNames, loadashOrderDirections);
            }
          } else {
            const defaultSortingValues = this.getDefaultSortValues(altOrderbyCols, childGroupField);

            rows = orderBy(rows, orderGroupNames.concat(defaultSortingValues.sortNames), defaultSortingValues.sortDirections);
          }

          let groups: IGroup[] = [];
          let currVal: string = null;
          for (let i = 0; i < rows.length; i++) {
            const formattedMainGroupValue: string | number = this.getGroupedColumnFormattedValue(mainGroupColum, rows[i][mainGroupColum.displayNameLowerCase]);
            if (formattedMainGroupValue?.toString().toLowerCase() != currVal?.toLowerCase()) {
              currVal = formattedMainGroupValue?.toString() ?? "";
              if (groups.length > 0) {
                const prevInd = groups.length - 1;
                groups[prevInd].count = i - groups[prevInd].startIndex;
              }
              const newGroup = {
                key: currVal ? currVal : `${this.localizedText.unassigned}-${groups.length}-key`,
                name: `${mainGroupColum.displayName} : ` + (currVal ? currVal : this.localizedText.unassigned),
                startIndex: i,
                count: -1,
                level: 0,
                children: [],
              };
              groups.push(newGroup);
            }
            if (i == rows.length - 1 && groups.length > 0) {
              groups[groups.length - 1].count = i - groups[groups.length - 1].startIndex + 1;
            }
          }
          //main groups have been created with counts and everything.
          if (childGroupField) { //if we have a child group property
            //get all the unique names in a main group
            groups.forEach((g, ind, arr) => {
              const allNames: any[] = rows
                .filter((r, i) => i >= g.startIndex && i < g.startIndex + g.count)
                .map(r => this.getGroupedColumnFormattedValue(childGroupColum, r[childGroupColum.displayNameLowerCase]));

              const uniqueTypes = Array.from(new Set(allNames)); //unique types/properties/values for this group we are working with
              let childrenGroups: IGroup[] = [];
              uniqueTypes.forEach(type => {
                const childRows = (r: any) => ((this.getGroupedColumnFormattedValue(mainGroupColum, (r[mainGroupColum.displayNameLowerCase])?.toString() ?? "") === (g.key.includes(this.localizedText.unassigned) ? "" : g.key)) && this.getGroupedColumnFormattedValue(childGroupColum, r[childGroupColum.displayNameLowerCase]) === type); //predicate function filter

                const count = rows.filter(childRows).length;
                childrenGroups.push({
                  key: type ? type.toString() : `${this.localizedText.unassigned}-${childrenGroups.length}-child`,
                  name: `${childGroupColum.displayName} : ` + (type ? type.toString() : this.localizedText.unassigned),
                  count: count,
                  startIndex: rows.findIndex(childRows),
                  level: 1,
                })
              })
              g.children = childrenGroups;
            })
          }
          this.groupSetter(groups);
        }
      } else {
        //no groupby in the view
        const defaultSortingValues = this.getDefaultSortValues(altOrderbyCols, null);
        rows = orderBy(rows, defaultSortingValues.sortNames, defaultSortingValues.sortDirections);
      }
    } catch (error) {
      logger(error, "DetailListSearchColumnSorterGrid.ts (setGroups)");
    }
    return rows;
  }

  private getDefaultSortValues = (altOrderbyCols: string[], childGroupField: any) => {
    //Apply default sorting
    let orderGroupNames: ((row: any) => string | number)[] = [];

    const altSorter = row => {
      const lcOrderProp = altOrderbyCols[0];
      if (typeof row[lcOrderProp] === "string") {
        return row[lcOrderProp].toLowerCase();
      } else {
        return row[lcOrderProp];
      }
    }
    const altSorter2 = row => {
      const lcOrderProp = altOrderbyCols[1];
      if (typeof row[lcOrderProp] === "string") {
        return row[lcOrderProp].toLowerCase();
      } else {
        return row[lcOrderProp];
      }
    }

    let defaultOrderGroupDirections: (boolean | "desc" | "asc")[] = [];

    if (altOrderbyCols.length > 0) {
      orderGroupNames.push(altSorter);
      defaultOrderGroupDirections.push(this.altSortOrder);

      if (altOrderbyCols.length > 1) {
        orderGroupNames.push(altSorter2);
        defaultOrderGroupDirections.push(this.altSortOrder2);
      }
    }

    let groupOrderDirections: (boolean | "desc" | "asc")[] = this.mainGroupSortOrder ? (childGroupField ? [this.mainGroupSortOrder, this.childGroupSortOrder] : [this.mainGroupSortOrder]) : [];

    let loadashOrderDirections = groupOrderDirections.concat(defaultOrderGroupDirections);

    return {
      sortNames: orderGroupNames,
      sortDirections: loadashOrderDirections
    };
  }

  private getGroupedColumnFormattedValue = (groupedColumn: IGroupedColumn, rowValue: any): string | number => {
    if (groupedColumn.type === SharePointInputTypes.datetime.toLowerCase()) {
      const unixValue: number = Number(rowValue);
      if (unixValue) {
        const unixDate = dayjs.unix(unixValue);
        if (unixDate)
          return unixDate.format("L");
      }
    }
    else if (groupedColumn.type === SharePointInputTypes.number.toLowerCase() || groupedColumn.type === SharePointInputTypes.currency.toLowerCase()) {
      const numberValue: number = Number(rowValue);
      if (numberValue) return numberValue;
    }
    return rowValue?.toString().trim() ?? "";
  }

  private getFieldsDisplayName = (selectedView: IViewsDropdown, fields: any) => {
    if (selectedView && selectedView.fields.length > 0) {
      //only do these expensive calcs if we actually must
      const fieldsDisplayName: IFieldDisplayname[] = selectedView.fields.map((fieldView, index) => {
        return ({
          ...fieldView,
          displayName: fields.find(field => {
            return field.InternalName?.toLowerCase() === fieldView?.Name?.toLowerCase()
          })?.xml.DisplayName as string,
          index,
        })
      })
      return fieldsDisplayName;
    }
    return null;
  }

  private getValues = (dataRows: any[], columns: string[]) => {
    let rows = [];
    dataRows.forEach((dataRow, index) => {
      let row = { key: index };
      columns.forEach(column => {
        row[column !== "sharepointIds" ? column.toLowerCase() : column] = dataRow[column]?.value !== undefined ? dataRow[column].value : dataRow[column];
      });

      rows.push(row);
    });

    return rows;
  };

  private onColumnClick = (_ev: React.MouseEvent<HTMLElement>, column: IColumn, selectedView: IViewsDropdown, fields: any[]): void => {
    const newColumns: IColumn[] = this.tableData.columns.slice();
    const currColumn: IColumn = newColumns.filter(currCol => column.key === currCol.key)[0];
    newColumns.forEach((newCol: IColumn) => {
      if (newCol === currColumn) {
        currColumn.isSortedDescending = !currColumn.isSortedDescending;
        currColumn.isSorted = true;
      } else {
        newCol.isSorted = false;
        newCol.isSortedDescending = true;
      }
    });
    let newItems: any[];
    if (selectedView && selectedView?.query?.GroupBy?.length > 0) {
      newItems = this.setOrderAndGroups(newColumns, selectedView, fields, this.tableData.rows, column, currColumn.isSortedDescending);
    } else {
      newItems = this.copyAndSort(this.tableData.rows, currColumn.fieldName!, currColumn.isSortedDescending);
    }

    let columns = newColumns.map(column => ({ ...this.setColumnWayOfDisplay(column) }));
    this.columnDispatcher(columns);
    this.rowDispatcher(newItems);
  };

  private copyAndSort<T>(items: T[], columnKey: string, isSortedDescending?: boolean): T[] {
    const isFileTypeSort: boolean = columnKey === "fileType";
    const key = isFileTypeSort
      ? "name" as keyof T
      : columnKey as keyof T;

    return items.slice(0).sort((a: T, b: T) => {
      const varA = !isFileTypeSort ? a[key] : getExtension(a[key]?.toString()); //needs to be done like this, because a[key] is of type T[keyof T], which does not have string functions. T[keyof T] types also cannot be cast to string, as it explicitly is stated as the typeof type T type in the function definition. But variables work differently. 
      const varB = !isFileTypeSort ? b[key] : getExtension(b[key]?.toString());
      if (typeof varA === 'string' && typeof varB === 'string') {
        return ((isSortedDescending ? (varA.toLowerCase()) < varB.toLowerCase() : varA.toLowerCase() > varB.toLowerCase()) ? 1 : -1);
      } else {
        return ((isSortedDescending ? a[key] < b[key] : a[key] > b[key]) ? 1 : -1);
      }

    })
  }

  private getFileExtension = (filename: string): string => {
    try {
      const parts = filename.split(".");
      if (parts.length > 0) {
        return parts[parts.length - 1];
      }
      return "";
    } catch {
      return "";
    }
  }

  private setColumnWayOfDisplay = (column: IColumn): IColumn => {
    if (column.fieldName === columnSearchTypes.name) {
      column.name = this.localizedText.name;
      column.minWidth = 250;
      column.onRender = item => {
        const fileExt: string = this.getFileExtension(item.name).toLowerCase();
        // const shouldGetPreview: boolean = validPreviewExtension.some(ext => fileExt === ext);

        // const tooltipProps: ITooltipProps = {
        //   onRenderContent: () => (
        //     <HoverPreview item={item} displayPreview={shouldGetPreview} />
        //   ),
        // };
        return (
          // <TooltipHost content={item.name} styles={{ root: { display: "inline-block", maxWidth: "100%", textOverflow: "ellipsis", overflow: "hidden", whiteSpace: "nowrap" } }} directionalHint={DirectionalHint.rightCenter}>
          <Stack horizontal verticalAlign="center" tokens={gridFileNameStackTokens}>
            <Stack.Item grow styles={gridFileNameStackItemStyles}>
              <Link
                rel="noopener"
                styles={detailRowLinkStyles}
                onClick={async () => {
                  if (fileExt === extensions.url || item[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.folder)) {
                    try {
                      const itemUrl = new URL(item[columnSearchTypes.webUrl]);
                      const url = await getGridItemUrl(item, itemUrl.pathname + itemUrl.search, itemUrl.origin);
                      window.open(url, "_blank");
                      return;
                    } catch (error) {
                      // ignore
                    }
                  }

                  const graphAccessToken: string = await authModule.getGraphAccessToken();
                  const itemId: string = item.itemid ?? await getFileItemIdFromRelativePath(graphAccessToken, item.driveid, item["serverrelativepath"]);

                  if (fileExt === extensions.pdf) {
                    OpenPdfInBrowser((await authModule.getAccount(false)), item.driveid, itemId);
                  }
                  else if (this.gridTenantSettings.openDocumentsInClientApp) {
                    const fileInfo = SearchOpenExtensionHelper.determineOfficeFileType(getLastSplittedStringItem(item.name, /[.]+/));
                    const url = await getListItemWebUrl(
                      graphAccessToken,
                      item.driveid,
                      itemId
                    );

                    OpenInBrowser(`${fileInfo.localUrlPrepended}${url}`, fileInfo.localUrlPrepended && generalModule.isInConentMangerMode());
                  }
                  else {
                    const url = await getItemWebViewUrl(
                      graphAccessToken,
                      item.driveid,
                      itemId
                    );

                    OpenInBrowser(url);
                  }
                }}
              >
                <TooltipHost content={item.name} overflowMode={TooltipOverflowMode.Parent}>
                  <span>{item.name}</span>
                </TooltipHost>
              </Link>
            </Stack.Item>
            <Stack.Item styles={gridOptionButtonStackItemStyles}>
              <TooltipHost content={this.localizedText.moreActions}>
                <IconButton
                  aria-label="Options"
                  className={detailsListClassNames.optionButton}
                  iconProps={{ iconName: 'MoreVertical' }}
                  onClick={(ev) => {
                    const pos: Point = { left: ev.clientX, top: ev.clientY };
                    this.triggerContextMenu(ev, pos);
                  }}
                />
              </TooltipHost>
            </Stack.Item>
          </Stack>

          // </TooltipHost>
        )
      };
    }
    else if (column.fieldName === columnSearchTypes.fileType) {
      column.onRender = item => {
        if (item[piiConfig.privacySentitiveColumnName.toLowerCase()] === 'False' && item[piiConfig.privacySentitiveDataColumnName.toLowerCase()] === null) {
          return <Spinner size={SpinnerSize.small} styles={spinnerStyles} />
        }
        else {
          return (item[columnSearchTypes.contentTypeId] && !item[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.folder)) || item.name.includes(".")
            ? (
              <Icon
                {...getFileTypeIconProps({
                  extension: getLastSplittedStringItem(item.name, /[.]+/),
                  size: 20,
                  imageFileType: "svg"
                })}
                styles={iconStyles}
              />
            )
            : (
              <Icon
                {...getFileTypeIconProps({
                  type: item[columnSearchTypes.contentTypeId]?.startsWith(SharePointFolderContentTypeIds.documentSet) ? FileIconType.docset : FileIconType.folder,
                  size: 20,
                  imageFileType: "svg"
                })}
                styles={iconStyles}
              />
            );
        }
      };
    }
    else if (column.fieldName === columnSearchTypes.sizeValue) {
      column.name = this.localizedText.size;
      column.data = "number";
      column.minWidth = 150;
      column.onRender = (item) => {
        return <span>{item.sizetext}</span>;
      };
    }
    else if (column.fieldName === columnSearchTypes.modifiedValue) {
      column.name = this.localizedText.modified;
      column.data = "number";
      column.minWidth = 150;
      column.onRender = (item) => {
        return <span>{item.modifiedtext}</span>;
      };
    }
    else if (column.fieldName === columnSearchTypes.modifiedBy) {
      column.name = this.localizedText.modifiedBy;
      column.minWidth = 150;
    }
    else if (column.data === "DateTime" || column.data === "DateOnly") {
      column.onRender = (item, index, column) => { // DateTime results are returned as a string representing a Unix timestamp
        const unixValue: number = Number(item[column.fieldName]);
        if (unixValue) {
          const unixDate = dayjs.unix(unixValue);
          return <span>{column.data === "DateOnly" ? unixDate.format("L") : unixDate.format("L LT")}</span>;
        }
        return null;
      };
    }
    else if ((column.data === "User" || column.data === "UserMulti") && column.fieldName !== "modified by" && column.fieldName !== "created by") {
      column.onRender = (item) => {
        return <div>
          <div style={pillContainer}>
            {((item[column.fieldName] as string)?.split(";") ?? []).map(usr => (
              <div key={`${item["uniqueid"]}_${usr}`} style={peoplePill}>
                <Persona styles={peoplePillPersonaStyles} text={usr} size={PersonaSize.size24} showOverflowTooltip={false} />
              </div>
            ))}
          </div>
        </div>
      }
    }
    else {
      column.minWidth = 150;
    }

    return column;
  };
}
